import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSnackBar } from "../../../../../hooks/useSnackBar";
import { readSession } from "../../../../../helpers/session";
import "./Orders.css";

// components imports
import OrdersTable from "../../../../../Components/BoltTable/BoltTable";
import PrintMenu from "../../../../../Components/PrintMenu/PrintMenu";
import BulkActionBar, {
  BulkActionButton,
} from "../../../../../Components/BoltTable/Components/BulkActionBar";
import InvoiceModal from "../../../../../Components/InvoiceModal/InvoiceModal";
import Confirm from "../../../../../Components/Popups/Confirm";
import AttachmentsModal from "../../../../Warehouse/WarehouseOrders/Components/AttachmentsModal/AttachmentsModal";

// apis imports
import { updateUser } from "../../../../../store/userSlice";
import {
  fetchAllOrders,
  deleteSingleOrder,
  fetchSingleOrder,
  fetchOrderStatuses,
  bulkArchiveOrders,
  bulkUnarchiveOrders,
} from "../../../../../services/orders.services";
import { fetchOrderCount } from "../../../../../services/warehouse.services";
import {
  fetchRoutesDropdown,
  updateOrderRoute,
} from "../../../../../services/ShipperServices/DeliveryRoutes/deliveryRoutes.services";

// library imports

import { AiOutlineUpload } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { PDFDocument } from "pdf-lib";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch, useSelector } from "react-redux";
import OrderHistoryModal from "./Components/OrderHistoryModal/OrderHistoryModal";
import DetailsModal from "../../../../Warehouse/WarehouseOrders/Components/OrderDetails/DetailsModal";
import { getAccessibleWarehouseIds } from "../../../../../helpers/warehousePermissions";
import ActionDropDown from "./Components/ActionsDropDown";
import FullfilmentBillingStatusModal from "./Components/FullfilmentBillingStatus/FullfilmentBillingStatus";
import FullfilmentTotalDetailsModal from "./Components/FullfilmentTotalDetails/FullfilmentTotalDetails";
import MarketPlaceDetails from "./Components/MarketPlace/marketplaceDetails";
import OrderCell from "./Components/Cells/OrderCell";
import CustomerCell from "./Components/Cells/CustomerCell";
import ItemsCell from "./Components/Cells/ItemsCell";
import StatusCell from "./Components/Cells/StatusCell";
import ShippingCell from "./Components/Cells/ShippingCell";
import FulfilmentCell from "./Components/Cells/FulfilmentCell";
import RouteDropdown, {
  moveRouteCount,
  routeFilterOptions,
} from "./Components/Cells/RouteDropdown";
import ActionsCell from "./Components/Cells/ActionsCell";
import {
  calculateTotalAdditionalCost,
  calculateTotalCost,
} from "./Components/Cells/fulfilmentCost";
import { formatCount, orderHeaderStats, withCounts } from "./orderHeaderStats";
import {
  archiveConfirmText,
  archiveResultText,
  archiveSelection,
  splitByArchived,
  unarchiveResultText,
} from "./archiveOrders";
import ImportModal from "./Components/ImportModal/Import";
import ManageRoutesModal from "./Components/DeliveryRoutes/ManageRoutesModal";
import {
  RUNS_DRIVE,
  RUNS_MANAGE,
  canArchiveOrders,
  canChangeOrderStatus,
  canDeleteOrders,
  canEditOrders,
  canShipOrders,
  hasStorePermission,
  seesOrderPrices,
  withStatusChoices,
} from "../../../../../helpers/storeAccess";
import RoutesBar from "./Components/DeliveryRoutes/RoutesBar";
import RouteSheetModal from "./Components/DeliveryRoutes/RouteSheetModal";
import AddToRunModal from "./Components/DeliveryRoutes/AddToRunModal";
import AddRunMenu from "./Components/DeliveryRoutes/AddRunMenu";
import TrackingLink from "./Components/DeliveryRoutes/TrackingLink";
import CreateRunModal from "./Components/DeliveryRoutes/CreateRunModal";
import RunChip from "./Components/DeliveryRoutes/RunChip";
import RunsModal from "./Components/DeliveryRoutes/RunsModal";
import { addedText } from "./Components/DeliveryRoutes/runOrder";
import {
  openRunStops,
  runFilterLabel,
  runFilterRuns,
  runTitle,
  upsertRun,
} from "./Components/DeliveryRoutes/runs";
import { fetchRuns } from "../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import { locationAddress } from "./Components/DeliveryRoutes/routeSheet";
import SelfDeliveryToggle from "./Components/DeliveryRoutes/SelfDeliveryToggle";
import ArchivedToggle, {
  ARCHIVED_ONLY,
  isArchivedOnly,
} from "./Components/ArchivedToggle";
import CarrierOrdersMenu, {
  NEWEST,
  firstChipValue,
  readCarrierFirst,
  saveCarrierFirst,
} from "./Components/CarrierOrdersMenu";
import { MdAdd, MdQrCodeScanner } from "react-icons/md";
import {
  carrierFilterOptions,
  fulfilmentBillingFilterOptions,
  fulfilmentFilterOptions,
  shipmentStatusFilterOptions,
  typeFilterOptions,
} from "../../../../../constants/filterOptions";

const orderStatusFilterOptions = [
  { label: "Confirmed", value: "confirmed" },
  { label: "Processing", value: "processing" },
  { label: "Cancelled", value: "cancelled" },
  // { label: "Fulfilled", value: "fulfilled" },
];

const countryOptions = [
  { label: "Canada", value: "Canada" },
  { label: "United States", value: "United States" },
];

const orderTypeOptions = [
  ...typeFilterOptions,
  { label: "Return", value: "return" },
  { label: "Walkin", value: "walkin" },
];

// The Type filter's value for self-delivery orders, set from the Order
// heading or the toolbar's Self delivery switch; while it is on, the routes
// bar shows.
const SELF_DELIVERY = "self delivery";
const isSelfDelivery = (filterValues) =>
  String(filterValues?.type ?? "")
    .trim()
    .toLowerCase() === SELF_DELIVERY;

// Fulfilment statuses for the Fulfilment heading (the old metrics cards
// also counted Cancelled and Archived).
const fulfilmentStatusOptions = [
  ...fulfilmentFilterOptions,
  { label: "Cancelled", value: "cancelled" },
  { label: "Archived", value: "archived" },
];
// The Fulfilment Status filter takes several statuses; this picks every one
// still to fulfil in one go.
const NOT_FULFILLED_PRESET = {
  label: "Not fulfilled yet",
  values: ["pending", "packing", "attention"],
  hint: "Pending / New, Packing and Attention",
};
const NOT_FULFILLED = NOT_FULFILLED_PRESET.values.join(",");

// The toolbar's Carriers switch: orders still to fulfil that leave with a
// carrier (the server's carrier filter, "any" carrier). Self delivery and
// Carriers exclude each other.
const CARRIER_ORDERS = "any";
const isCarrierOrders = (filterValues) =>
  String(filterValues?.carrier ?? "") === CARRIER_ORDERS;

// Known values for the Status heading filters; any other text can be typed
// (the backend matches both fields by partial text).
const orderStatusHeaderOptions = [
  ...orderStatusFilterOptions,
  { label: "Completed", value: "completed" },
];
const marketplaceStatusHeaderOptions = [
  { label: "Processing", value: "processing" },
  { label: "Fulfilled", value: "fulfilled" },
];

const Orders = () => {
  const user = useSelector((state) => state.user);
  // A store's users plan runs and record their stops as their roles allow.
  const canManageRuns = hasStorePermission(user, RUNS_MANAGE);
  const canDriveRuns = hasStorePermission(user, RUNS_DRIVE);
  // Prices and amounts need "View price"; creating a shipment (the carriers'
  // rates) "Ship orders" as well. The server sends hidden fields blank.
  const showPrices = seesOrderPrices(user);
  const canShip = canShipOrders(user);
  // What the role may do to orders (the server checks the same): change the
  // fulfilment status (Cancelled apart), archive, edit, delete.
  const canChangeStatus = canChangeOrderStatus(user);
  const canArchive = canArchiveOrders(user);
  const canEdit = canEditOrders(user);
  const canDelete = canDeleteOrders(user);
  const [orders, setOrders] = useState([]);
  const [actionLoader, setActionLoader] = useState(false);

  const [prodHistoryModal, setProdHistoryModal] = useState(false);
  const [fulBillingModal, setFulBillingModal] = useState(false);
  const [marketplaceModal, setMarketplaceModal] = useState(false);
  const [fulBillingDetailsModal, setFulBillingDetailsModal] = useState(false);

  const [detailModal, setdetailModal] = useState(false);
  // An order opened from a route sheet, which may not be on this page of the
  // list: the order view's Fulfilment rights are checked on it.
  const [detailFromSheet, setDetailFromSheet] = useState(null);
  const [labelDownloading, setLabelDownloading] = useState("");

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoader, setdeleteLoader] = useState(false);
  const [imagesModal, setImagesModal] = useState();

  const [orderCounts, setOrderCounts] = useState(null);
  const headerStats = useMemo(
    () => orderHeaderStats(orderCounts),
    [orderCounts],
  );
  // Self-delivery orders, all time (the Type heading's count), for the
  // routes bar's All tab.
  const selfDeliveryCount = useMemo(
    () =>
      orderCounts
        ? (orderCounts.by_type ?? [])
            .filter((item) => isSelfDelivery({ type: item?.type }))
            .reduce((sum, item) => sum + (Number(item?.total_orders) || 0), 0)
        : null,
    [orderCounts],
  );
  const [Import, setImport] = useState(false);
  const [routesOpen, setRoutesOpen] = useState(false);
  const [routes, setRoutes] = useState([]);
  // True once the routes list has loaded: the routes bar waits for it, so it
  // never shows controls that can't work (e.g. before the routes backend).
  const [routesReady, setRoutesReady] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [refresh, setRefresh] = useState();
  // Which orders the list shows first (newest, every carrier's, or one
  // carrier's), kept on this device for this login.
  const [carrierFirst, setCarrierFirst] = useState(() =>
    readCarrierFirst(user?.id, carrierFilterOptions),
  );
  const changeCarrierFirst = (value) => {
    setCarrierFirst(value);
    saveCarrierFirst(user?.id, value);
    setRefresh(Date.now());
  };
  // Shown as a chip with the list's filters ("Show first: UPS"); its × and
  // Clear all go back to the newest orders first.
  const carrierFirstChips = useMemo(
    () =>
      carrierFirst
        ? [
            {
              key: "carrier_first",
              label: "Show first",
              value: firstChipValue(carrierFirst, carrierFilterOptions),
              onRemove: () => changeCarrierFirst(NEWEST),
            },
          ]
        : [],
    // changeCarrierFirst only sets state and saves the choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carrierFirst],
  );
  // Delivery runs: on once the orders come with open_run_stop (servers
  // without runs don't send it), and the pop-ups' state.
  const [runsSupported, setRunsSupported] = useState(false);
  // The runs the Shipping column's Run filter offers (see runFilterRuns).
  const [filterRuns, setFilterRuns] = useState([]);
  // The table's own filter setter (BoltTable's filterApi), for the route
  // sheet's "Show in orders list".
  const tableFilters = useRef(null);
  const [runsOpen, setRunsOpen] = useState(false);
  const [openRunId, setOpenRunId] = useState(null);
  const [createRunIds, setCreateRunIds] = useState(null);
  // A run started from one order's Add to run menu: once it's created the
  // ticks stay and the route sheet stays shut, so more orders can be added.
  const [createRunSolo, setCreateRunSolo] = useState(false);
  const [addToRunRows, setAddToRunRows] = useState(null);

  const [showLabelOptions, setShowLabelOptions] = useState(false);
  const [isBulkLabelMerging, setIsBulkLabelMerging] = useState(false);
  const [bulkLabelProgress, setBulkLabelProgress] = useState("");
  // Bulk Archive: the confirm pop-up's split of the ticked orders, while open.
  const [archivePlan, setArchivePlan] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);

  useEffect(() => {
    if (selectedRows.length === 0) setShowLabelOptions(false);
  }, [selectedRows]);

  const setSnack = useSnackBar();

  // Used by the commented-out columns below.
  // eslint-disable-next-line no-unused-vars
  const defaultWidth = "200px";

  function isCancelled(status) {
    const lowerCaseStatus = status ? status.toLowerCase() : null;
    return lowerCaseStatus === "cancelled";
  }

  // Dropdown filter options. Static enums use canonical backend values; carrier
  // and warehouse come from the account's own lists.

  const warehouseFilterOptions = (user?.user_approved_warehouses || [])
    .map((w) => ({ label: w?.warehouse?.name, value: w?.warehouse?.name }))
    .filter((o) => o.label);

  const columns = [
    // {
    //   id: 0,
    //   label: "#",
    //   field_name: "id",
    //   render: (row) => <>{row?.id ?? "-"}</>,
    //   width: defaultWidth,
    //   disabled: (row) => isCancelled(row?.status),
    // },
    {
      id: 1,
      label: "Order",
      headerStats: headerStats.order,
      field_name: "order_id",
      // Type, Marketplace and Created live in this cell now. The heading
      // pop-up holds the order number search and Type (Return is listed: the
      // old Type header search was the only way to filter it).
      headerFilters: [
        { label: "Order Number", field_name: "order_id" },
        {
          label: "Type",
          field_name: "type",
          options: withCounts(orderTypeOptions, orderCounts, "type"),
        },
        // Set by the toolbar's Archived switch: archived orders are out of
        // the list unless asked for (their status is unchanged).
        {
          label: "Archived",
          field_name: "archived",
          options: [{ label: "Only archived orders", value: ARCHIVED_ONLY }],
        },
      ],
      render: (row) => (
        <OrderCell
          row={row}
          // The order number opens the order view (the redesigned one).
          onOpen={(order) =>
            setdetailModal([order?.id, false, undefined, true])
          }
          onOpenMarketplace={setMarketplaceModal}
        />
      ),
      width: "210px",
      headerColor: "var(--color-brand-light)",
      fixed: "left",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 3,
      label: "Customer",
      field_name: "first_name",
      headerFilters: [
        { label: "Name", field_name: "first_name" },
        // Matches the country code or name by partial text (backend).
        { label: "Country", field_name: "country", options: countryOptions },
      ],
      render: (row) => <CustomerCell row={row} />,
      width: "146px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 23,
      label: "Items",
      render: (row) => <ItemsCell row={row} />,
      width: "196px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 4,
      label: "Status",
      field_name: "status",
      // Order status and Marketplace status (merged in here) each keep
      // their filter in the heading pop-up.
      headerFilters: [
        {
          label: "Order Status",
          field_name: "status",
          options: orderStatusHeaderOptions,
        },
        {
          label: "Marketplace Status",
          field_name: "market_place_status",
          options: marketplaceStatusHeaderOptions,
        },
      ],
      render: (row) => <StatusCell row={row} />,
      width: "176px",
      disabled: (row) => isCancelled(row?.status),
    },
    // {
    //   id: 5,
    //   label: "Amount Pending",
    //   field_name: "amount_pending",
    //   render: (row) => <>{row?.amount_pending ?? "-"}</>,
    //   width: defaultWidth,
    //   disabled: (row) => isCancelled(row?.status),
    // },
    // {
    //   id: 6,
    //   label: "Payment Received",
    //   field_name: "amount_received",
    //   render: (row) => <>{row?.amount_received ?? "-"}</>,
    //   width: defaultWidth,
    //   disabled: (row) => isCancelled(row?.status),
    // },
    // {
    //   id: 7,
    //   label: "Method",
    //   field_name: "payment_method_title",
    //   render: (row) => <>{row?.payment_method_title ?? "-"}</>,
    //   width: defaultWidth,
    //   disabled: (row) => isCancelled(row?.status),
    // },
    {
      id: 10,
      label: "Shipping",
      headerStats: headerStats.shipping,
      // Not a filter field, so chips take the heading sections' labels.
      field_name: "shipping",
      // Carrier, Packages, Tracking, Reference, Shipment Status and Total
      // live in this cell now; all their filters are in the heading pop-up.
      headerFilters: [
        { label: "Tracking Number", field_name: "shipment_tracking_id" },
        { label: "Reference", field_name: "referance_code" },
        {
          label: "Carrier",
          field_name: "company_name",
          options: carrierFilterOptions,
        },
        // Set by the toolbar's Carriers switch (orders that leave with a
        // carrier); here so its chip reads well and it can be cleared.
        {
          label: "Carrier orders",
          field_name: "carrier",
          options: [{ label: "Any carrier", value: CARRIER_ORDERS }],
        },
        {
          label: "Shipment Status",
          field_name: "shipment_status",
          options: shipmentStatusFilterOptions,
        },
        // Only once the account's routes have loaded (none to pick
        // otherwise, and servers without routes ignore route_name).
        ...(routes.length
          ? [
              {
                label: "Route",
                field_name: "route_name",
                options: routeFilterOptions(routes).map((option) => ({
                  ...option,
                  count:
                    option.count == null
                      ? undefined
                      : formatCount(option.count),
                })),
              },
            ]
          : []),
        ...(runsSupported
          ? [
              {
                label: "Run",
                field_name: "run_state",
                // A state, or one run (its id): the orders on it.
                options: [
                  { label: "On an open run", value: "open" },
                  { label: "Not on a run", value: "none" },
                  ...runFilterRuns(filterRuns).map((run) => ({
                    label: runFilterLabel(run),
                    value: String(run.id),
                  })),
                ],
                freeText: false,
              },
            ]
          : []),
      ],
      render: (row) => (
        <ShippingCell
          row={row}
          onCreateShipment={canShip ? handleOrderShipemnt : undefined}
          showAmount={showPrices}
          routeControl={
            <>
              <RouteDropdown
                row={row}
                routes={routes}
                onChange={handleRouteChange}
                disabled={isCancelled(row?.status)}
              />
              <RunChip stop={row?.open_run_stop} onOpen={setOpenRunId} />
              <TrackingLink
                code={row?.self_delivery_tracking?.tracking_id}
                onCopied={(text) =>
                  setSnack((snack) => ({
                    ...snack,
                    open: true,
                    text,
                    severity: "success",
                  }))
                }
                onFailed={() =>
                  setSnack((snack) => ({
                    ...snack,
                    open: true,
                    text: "Couldn't copy the tracking link",
                    severity: "error",
                  }))
                }
              />
              {runsSupported && canManageRuns && (
                <AddRunMenu
                  row={row}
                  locations={routeLocations}
                  onNewRun={(order) => {
                    setCreateRunSolo(true);
                    setCreateRunIds([order.id]);
                  }}
                  onAdded={(run, sorting) => {
                    applyRun(run);
                    setSnack((snack) => ({
                      ...snack,
                      open: true,
                      text: addedText(`#${row?.id}`, runTitle(run), sorting),
                      severity: "success",
                    }));
                  }}
                  onError={(text) =>
                    setSnack((snack) => ({
                      ...snack,
                      open: true,
                      text,
                      severity: "error",
                    }))
                  }
                />
              )}
            </>
          }
        />
      ),
      width: "290px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 16,
      label: "Fulfilment",
      headerStats: headerStats.fulfilment,
      // Not a filter field, so chips take the heading sections' labels.
      field_name: "fulfilment",
      // Warehouse (now "Location"), Fulfilment, Total and Billing live in
      // this cell now; all their filters are in the heading pop-up.
      headerFilters: [
        {
          label: "Fulfilment Status",
          field_name: "fulfillment_status",
          options: withCounts(
            fulfilmentStatusOptions,
            orderCounts,
            "fulfilment",
          ),
          multiple: true,
          presets: [NOT_FULFILLED_PRESET],
        },
        {
          label: "Location",
          field_name: "warehouse_name",
          options: warehouseFilterOptions,
        },
        {
          // The server matches billing exactly, so typed text can't help.
          label: "Billing",
          field_name: "fulfillment_billing_status",
          options: withCounts(
            fulfilmentBillingFilterOptions,
            orderCounts,
            "billing",
          ),
          freeText: false,
        },
      ],
      render: (row) => (
        <FulfilmentCell
          row={row}
          orderStatus={statusChoices}
          canEdit={
            canChangeStatus &&
            fulfillableWarehouseIds.has(String(row?.warehouse_id))
          }
          isDefaultLocation={defaultWarehouseIds.has(String(row?.warehouse_id))}
          onStatusChange={handlestatusChange}
          onOpenCost={setFulBillingDetailsModal}
          showCharges={showPrices}
          onOpenBilling={setFulBillingModal}
        />
      ),
      width: "240px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 22,
      label: "Actions",
      field_name: "actions",
      // Attachments live in this cell now (no filter to keep).
      render: (row) => (
        <ActionsCell
          row={row}
          onOpenAttachments={setImagesModal}
          menu={
            <ActionDropDown
              setdetailModal={setdetailModal}
              setProdHistoryModal={setProdHistoryModal}
              setConfirmDelete={setConfirmDelete}
              setInvoiceModal={setInvoiceModal}
              handleOrderShipemnt={handleOrderShipemnt}
              printLabel={printLabel}
              actionLoader={actionLoader}
              printDeclaration={printDeclaration}
              labelDownloading={labelDownloading}
              CircularProgress={CircularProgress}
              row={row}
              compact={true}
              canShip={canShip}
              showPrices={showPrices}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          }
        />
      ),
      width: "84px",
      fixed: "right",
      disabled: (row) => isCancelled(row?.status),
    },
  ];

  const getAllOrders = async (page, perPage, filter, nestedSearch) => {
    // Order status is filtered from the Status heading (it replaced the
    // All / Confirmed / Processing / Cancelled tabs, same &status= param).
    let response = await fetchAllOrders(
      page,
      perPage,
      filter,
      (nestedSearch || "") +
        (carrierFirst
          ? `&carrier_first=${encodeURIComponent(carrierFirst)}`
          : ""),
    );
    if (!response.error) {
      const { data, ...newObject } = response?.data?.result?.orderData;
      dispatch(
        updateUser({
          unReadOrders: response?.data?.result?.unReadOrdersCount,
        }),
      );
      setOrders(response?.data?.result?.orderData);
      if (
        (response?.data?.result?.orderData?.data ?? []).some(
          (order) => order && "open_run_stop" in order,
        )
      ) {
        setRunsSupported(true);
      }
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: "Something went wrong",
          severity: "error",
        };
      });
    }
  };

  const [invoiceModal, setInvoiceModal] = useState();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fulfillableWarehouseIds = useMemo(
    () => getAccessibleWarehouseIds(user, "fulfill_order"),
    [user],
  );
  // The account's own (default) locations: no fulfilment fee or billing.
  const defaultWarehouseIds = useMemo(
    () => getAccessibleWarehouseIds(user),
    [user],
  );

  // Route sheet for the ticked orders, when they're all self delivery: the
  // run starts and ends at one of the account's locations (own ones first).
  const [routeSheetIds, setRouteSheetIds] = useState(null);
  const routeLocations = useMemo(() => {
    const entries = Array.isArray(user?.user_approved_warehouses)
      ? user.user_approved_warehouses
      : [];
    const isOwn = (entry) =>
      defaultWarehouseIds.has(String(entry?.warehouse_id));
    return [...entries]
      .sort((a, b) => Number(isOwn(b)) - Number(isOwn(a)))
      .map((entry) => ({
        id: entry?.warehouse_id,
        name:
          entry?.warehouse?.name ||
          entry?.warehouse?.code ||
          `Location #${entry?.warehouse_id}`,
        address: locationAddress(entry?.warehouse),
      }));
  }, [user, defaultWarehouseIds]);
  // The ticked orders that are archived (Unarchive) and not (Archive).
  const selectedSplit = splitByArchived(selectedRows, orders?.data);
  const selectedAreSelfDelivery =
    selectedRows.length > 0 &&
    selectedRows.every((id) =>
      isSelfDelivery({
        type: orders?.data?.find((order) => order?.id === id)?.type,
      }),
    );
  // A run was created or changed: each of its orders shows its stop (or no
  // run, once the run is closed or the order came off it).
  const applyRun = (run, removedIds = []) => {
    if (run?.id != null) setFilterRuns((list) => upsertRun(list, run));
    const stops = openRunStops(run);
    setOrders((prevOrders) => ({
      ...prevOrders,
      data: prevOrders?.data?.map((order) =>
        order?.id in stops
          ? { ...order, open_run_stop: stops[order.id] }
          : removedIds.includes(order?.id)
            ? { ...order, open_run_stop: null }
            : order,
      ),
    }));
  };
  // The route sheet's "Show in orders list": the run's orders, through the
  // Run filter. Other filters stay, but not the route: a run's orders are on
  // its route anyway, and an old route filter could hide some.
  const showRunOrders = (run) => {
    if (run?.id == null) return;
    setFilterRuns((list) => upsertRun(list, run));
    setRouteSheetIds(null);
    setOpenRunId(null);
    tableFilters.current?.({
      type: SELF_DELIVERY,
      route_name: "",
      run_state: String(run.id),
    });
  };
  const runSaved = (run, text) => {
    if (run?.id == null) return;
    applyRun(run);
    setSelectedRows([]);
    setSnack((snack) => ({ ...snack, open: true, text, severity: "success" }));
    setOpenRunId(run.id);
  };
  const [orderStatus, setorderStatus] = useState([]);
  // The Fulfilment column's dropdown: statuses the role can't pick are
  // greyed out.
  const statusChoices = useMemo(
    () => withStatusChoices(user, orderStatus),
    [user, orderStatus],
  );
  const Navigate = useNavigate();

  // The Run filter's runs: read once the server is known to have runs.
  useEffect(() => {
    if (!runsSupported) return undefined;
    let cancelled = false;
    fetchRuns().then((response) => {
      const list = response.error ? null : response?.data?.result?.data;
      if (!cancelled && Array.isArray(list)) setFilterRuns(list);
    });
    return () => {
      cancelled = true;
    };
  }, [runsSupported]);

  useEffect(() => {
    const getOrderStatuses = async () => {
      const response = await fetchOrderStatuses();
      if (!response.error) setorderStatus(response?.data?.result ?? []);
    };
    getOrderStatuses();
  }, []);

  // The orders list PDF: the ticked orders on this page, or else every order
  // on it (see print/makeDocument, which loads on the first print).
  const makeOrdersPrint = useCallback(
    async ({ level, lang }) => {
      const { makeOrdersDocument } = await import("./print/makeDocument");
      const rows = orders?.data ?? [];
      const ticked = rows.filter((row) => selectedRows.includes(row.id));
      const user = readSession().user ?? {};
      return makeOrdersDocument({
        orders: ticked.length ? ticked : rows,
        selected: ticked.length > 0,
        statuses: orderStatus,
        level,
        lang,
        printedBy:
          [user.firstname, user.lastname].filter(Boolean).join(" ") ||
          user.username ||
          "",
      });
    },
    [orders, selectedRows, orderStatus],
  );

  // Delivery routes for the route dropdown and the Route filter in the
  // Shipping column: loaded with the page, and again after Manage routes
  // closes (routes may have been added, renamed or deleted there).
  const getRoutes = useCallback(async () => {
    const response = await fetchRoutesDropdown();
    const list = response?.data?.result?.data;
    if (response.error || !Array.isArray(list)) return;
    setRoutes(list);
    setRoutesReady(true);
  }, []);

  useEffect(() => {
    getRoutes();
  }, [getRoutes]);

  // Saves the route picked in the Shipping column and updates that row in
  // place, so the table stays on its page.
  const handleRouteChange = async (row, routeId) => {
    const response = await updateOrderRoute(row?.id, routeId);
    if (response.error) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text:
          response?.error?.response?.data?.message ??
          "Couldn't update the route",
        severity: "error",
      }));
      return;
    }
    const updated = response?.data?.result;
    const route =
      routeId == null
        ? null
        : (updated?.route ??
          routes.find((item) => String(item.id) === String(routeId)) ??
          null);
    setOrders((prevOrders) => ({
      ...prevOrders,
      data: prevOrders?.data?.map((order) =>
        order?.id === row?.id
          ? { ...order, route_id: route?.id ?? null, route }
          : order,
      ),
    }));
    // The route counts follow the move without reloading the routes.
    setRoutes((current) =>
      moveRouteCount(current, row?.route_id ?? row?.route?.id, route?.id),
    );
    setSnack((snack) => ({
      ...snack,
      open: true,
      text: route
        ? `#${row?.id} assigned to ${route.name}`
        : `#${row?.id} removed from its route`,
      severity: "success",
    }));
  };

  const handlestatusChange = (e, row) => {
    setdetailModal([row?.id, false, e.target.value]);
  };

  const printLabel = async (row) => {
    try {
      setActionLoader(row.id);
      fetch(row?.shipment?.single_shipping_labels.file, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      })
        .then((url) => url.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${
              row?.shipment?.tattension +
              " " +
              row?.shipment?.shipment_tracking_id
            } Label.pdf`,
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          setActionLoader(false);
        });
    } catch (error) {
      setSnack((snack) => {
        setActionLoader(false);
        return {
          ...snack,
          open: true,
          text: "Something went wrong try again later",
          severity: "error",
        };
      });
    }
  };
  const printDeclaration = async (row) => {
    setLabelDownloading("Declaration" + row?.id);
    try {
      const fileUrl = row?.shipment?.single_shipping_invoice?.file;
      if (!fileUrl) return;

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${
          row?.shipment?.tattension + " " + row?.shipment?.shipment_tracking_id
        } Declaration.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setLabelDownloading("");
    } catch (error) {
      setLabelDownloading("");
    }
  };

  const handleBulkLabelMerged = async () => {
    setShowLabelOptions(false);
    setIsBulkLabelMerging(true);
    setBulkLabelProgress("PREPARING...");

    try {
      const selectedOrders = orders?.data?.filter((o) =>
        selectedRows.includes(o.id),
      );
      const mergedPdf = await PDFDocument.create();
      let hasLabels = false;

      setBulkLabelProgress("FETCHING...");

      for (const row of selectedOrders) {
        const labelUrls = [];
        if (row?.shipment?.single_shipping_labels?.file) {
          labelUrls.push(row.shipment.single_shipping_labels.file);
        } else if (row?.manual_shipment?.shipping_labels?.length > 0) {
          row.manual_shipment.shipping_labels.forEach((l) =>
            labelUrls.push(l.file),
          );
        }

        for (const url of labelUrls) {
          try {
            const pdfBytes = await fetch(url).then((res) => res.arrayBuffer());
            const pdf = await PDFDocument.load(pdfBytes);
            const copiedPages = await mergedPdf.copyPages(
              pdf,
              pdf.getPageIndices(),
            );
            copiedPages.forEach((page) => mergedPdf.addPage(page));
            hasLabels = true;
          } catch (err) {
            console.error("Error loading label PDF:", url, err);
          }
        }
      }

      if (hasLabels) {
        setBulkLabelProgress("SAVING...");
        const mergedPdfBytes = await mergedPdf.save();
        const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Merged_Labels_${new Date().getTime()}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        setSnack({
          open: true,
          text: "No labels found for selected orders",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error merging labels:", error);
      setSnack({ open: true, text: "Error merging labels", severity: "error" });
    } finally {
      setIsBulkLabelMerging(false);
      setBulkLabelProgress("");
    }
  };

  const handleBulkLabel = async () => {
    setShowLabelOptions(false);
    setIsBulkLabelMerging(true);
    setBulkLabelProgress("DOWNLOADING...");

    try {
      const selectedOrders = orders?.data?.filter((o) =>
        selectedRows.includes(o.id),
      );
      let count = 0;
      const total = selectedOrders.length;

      for (const row of selectedOrders) {
        count++;
        setBulkLabelProgress(`${count}/${total}`);
        if (row?.shipment?.single_shipping_labels?.file) {
          await printLabel(row);
        } else if (row?.manual_shipment?.shipping_labels?.length > 0) {
          for (const [
            index,
            label,
          ] of row.manual_shipment.shipping_labels.entries()) {
            const response = await fetch(label.file);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${row.manual_shipment.shipment_tracking_id}_Label_${index + 1}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // Small delay for browser stability
            await new Promise((r) => setTimeout(r, 300));
          }
        }
      }
    } catch (error) {
      console.error("Error downloading individual labels:", error);
      setSnack({
        open: true,
        text: "Error downloading labels",
        severity: "error",
      });
    } finally {
      setIsBulkLabelMerging(false);
      setBulkLabelProgress("");
    }
  };

  const handleBulkPackingList = async () => {
    if (selectedRows.length > 0) {
      setdetailModal([selectedRows, true]);
    }
  };

  // Archive in the bulk bar: asks first, unless none of the ticked orders
  // can be archived from this account.
  const openBulkArchive = () => {
    const plan = archiveSelection(
      splitByArchived(selectedRows, orders?.data).notArchived,
      orders?.data,
      fulfillableWarehouseIds,
    );
    if (!plan.ids.length) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: "These orders are at a warehouse you can't fulfil from, so they can't be archived here",
        severity: "warning",
      }));
      return;
    }
    setArchivePlan(plan);
  };

  const handleBulkArchive = async () => {
    if (!archivePlan) return;
    setArchiving(true);
    const response = await bulkArchiveOrders(archivePlan.ids);
    setArchiving(false);
    if (response.error || !response?.data?.success) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text:
          response?.error?.response?.data?.message ??
          "Couldn't archive these orders",
        severity: "error",
      }));
      return;
    }
    setSnack((snack) => ({
      ...snack,
      open: true,
      text: archiveResultText(
        response?.data?.result,
        archivePlan.locked.length,
      ),
      severity: "success",
    }));
    setArchivePlan(null);
    setSelectedRows([]);
    // Reload with the same filters, so archived orders leave e.g. a Pending
    // view, and the heading numbers follow.
    setRefresh(Date.now());
    loadOrderCounts();
  };

  // Unarchive in the bulk bar: the ticked archived orders come back to the
  // list (nothing else about them changes), so no question first.
  const handleBulkUnarchive = async () => {
    const { archived } = splitByArchived(selectedRows, orders?.data);
    if (!archived.length) return;
    setUnarchiving(true);
    const response = await bulkUnarchiveOrders(archived);
    setUnarchiving(false);
    if (response.error || !response?.data?.success) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text:
          response?.error?.response?.data?.message ??
          "Couldn't unarchive these orders",
        severity: "error",
      }));
      return;
    }
    setSnack((snack) => ({
      ...snack,
      open: true,
      text: unarchiveResultText(response?.data?.result),
      severity: "success",
    }));
    setSelectedRows([]);
    setRefresh(Date.now());
    loadOrderCounts();
  };
  const handleOrderShipemnt = async (row) => {
    try {
      let data = await fetchSingleOrder(row?.id);
      if (Object.keys(data?.data?.result).length !== 0) {
        localStorage.setItem(
          "OrderShipment",
          JSON.stringify(data?.data?.result),
        );
        Navigate("/");
      } else {
        // Handle the case where data is empty
        setSnack((snack) => ({
          ...snack,
          open: true,
          text: "No order data available",
          severity: "warning",
        }));
      }
    } catch (error) {
      // Handle any errors that occur during the API call
      console.error("Error fetching order data:", error);
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: "Something went wrong",
        severity: "error",
      }));
    }
  };

  const handleDeleteRequest = async (data) => {
    if (data.id) {
      setdeleteLoader(true);
      const response = await deleteSingleOrder(data.id);

      if (!response.error && response?.data?.success) {
        setOrders((prevOrders) => {
          return {
            ...prevOrders,
            data: prevOrders?.data?.filter((d) => d?.id != data?.id),
          };
        });
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: "Order deleted successfully",
            severity: "success",
          };
        });
        setdeleteLoader(false);
        setConfirmDelete(false);
      } else {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text:
              response?.error?.response?.data?.message ??
              "Couldn't cancel this request!",
            severity: "error",
          };
        });
        setdeleteLoader(false);
      }
    }
  };

  const canFulfillDetailOrder = () => {
    if (!detailModal) return false;
    const [first] = detailModal;
    const listed = Array.isArray(first)
      ? first
      : (orders?.data ?? []).filter((o) => o.id === first);
    const orderList =
      listed.length || detailFromSheet?.id !== first
        ? listed
        : [detailFromSheet];
    if (!orderList.length) return false;
    return orderList.every((o) =>
      fulfillableWarehouseIds.has(String(o?.warehouse_id)),
    );
  };

  // The heading numbers cover all dates, like the list (no dates sent).
  // Loaded with the page, and again after a bulk Archive.
  const loadOrderCounts = useCallback(async () => {
    const { data, error } = await fetchOrderCount("", "");
    if (data) {
      setOrderCounts(data.result);
    } else {
      console.error("Error fetching order count:", error);
    }
  }, []);

  useEffect(() => {
    loadOrderCounts();
  }, [loadOrderCounts]);

  return (
    <div className="dispatcher__screen !max-w-full !px-[5px] !pt-[5px] !pb-0">
      <AttachmentsModal
        open={imagesModal}
        setOpen={setImagesModal}
        setSnack={setSnack}
        setOrders={setOrders}
      />
      <OrderHistoryModal
        open={prodHistoryModal}
        setOpen={setProdHistoryModal}
        setSnack={setSnack}
        setOrders={setOrders}
      />
      <FullfilmentBillingStatusModal
        open={fulBillingModal}
        setOpen={setFulBillingModal}
        setSnack={setSnack}
        setOrders={setOrders}
        orders={orders}
      />
      <MarketPlaceDetails
        open={marketplaceModal}
        setOpen={setMarketplaceModal}
        showPrices={showPrices}
      />
      <FullfilmentTotalDetailsModal
        open={fulBillingDetailsModal}
        setOpen={setFulBillingDetailsModal}
        calculateTotalCost={calculateTotalCost}
        calculateTotalAdditionalCost={calculateTotalAdditionalCost}
      />
      <DetailsModal
        open={detailModal}
        setOpen={setdetailModal}
        orders={orders}
        setOrders={setOrders}
        setSnack={setSnack}
        editable={canChangeStatus && canFulfillDetailOrder()}
      />
      <InvoiceModal
        open={!!invoiceModal}
        data={invoiceModal}
        setOpen={setInvoiceModal}
        actions={false}
        setSnack={setSnack}
      />
      <Confirm
        open={confirmDelete}
        setOpen={setConfirmDelete}
        handleConfirm={() => handleDeleteRequest(confirmDelete)}
        text="Do You Want To Delete This Order ? Deleting it will result in the removal of all associated tracks."
        loader={deleteLoader}
      />
      <Confirm
        open={Boolean(archivePlan)}
        setOpen={(open) => !open && setArchivePlan(null)}
        handleConfirm={handleBulkArchive}
        text={archivePlan ? archiveConfirmText(archivePlan) : ""}
        loader={archiving}
      />
      <ImportModal
        open={Import}
        setOpen={setImport}
        setSnack={setSnack}
        setRefresh={setRefresh}
      />
      <ManageRoutesModal
        open={routesOpen}
        onClose={() => {
          setRoutesOpen(false);
          getRoutes();
        }}
      />
      <div className="custom__table_size1 actionbtn__needtobe_fixwidth">
        <OrdersTable
          tableTitle="Orders"
          // New key for the 7-column layout: column ids 1, 4, 10 and 16 now
          // mean merged columns, so settings saved for the old 20 columns
          // must not hide them.
          tableId="ordersTableforShipper_v2"
          message={"No Orders Available To Display!"}
          searchPlaceHolder={"Search Orders By Order ID"}
          getAllData={getAllOrders}
          data={orders}
          columns={columns}
          refresh={refresh}
          selectable={true}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          // "Show first" shows with the filters and goes with Clear all.
          extraFilterChips={carrierFirstChips}
          onClearAllFilters={() => {
            if (carrierFirst) changeCarrierFirst(NEWEST);
          }}
          // alert="✨ New: Select multiple orders to bulk download labels & packing lists"
          headerButtons={({ values, setFilters }) => {
            tableFilters.current = setFilters;
            return (
              <div className="flex gap-[7px] flex-wrap items-center">
                <PrintMenu
                  variant="outline"
                  size="sm"
                  placement="down"
                  title={
                    selectedRows.length
                      ? "Print the ticked orders"
                      : "Print the orders on this page"
                  }
                  onMake={makeOrdersPrint}
                  disabled={!orders?.data?.length}
                />
                <SelfDeliveryToggle
                  on={isSelfDelivery(values)}
                  // Off goes back to all orders: the route filter goes too.
                  onChange={(on) =>
                    setFilters(
                      on
                        ? { type: SELF_DELIVERY, carrier: "" }
                        : { type: "", route_name: "" },
                    )
                  }
                />
                <CarrierOrdersMenu
                  on={isCarrierOrders(values)}
                  // On: carrier orders still to fulfil. Off: back to all
                  // orders (the fulfilment status goes too, unless it was
                  // changed meanwhile).
                  onChange={(on) =>
                    setFilters(
                      on
                        ? {
                            carrier: CARRIER_ORDERS,
                            fulfillment_status: NOT_FULFILLED,
                            type: "",
                            route_name: "",
                          }
                        : {
                            carrier: "",
                            ...(values?.fulfillment_status === NOT_FULFILLED
                              ? { fulfillment_status: "" }
                              : {}),
                          },
                    )
                  }
                  first={carrierFirst}
                  onFirstChange={changeCarrierFirst}
                  carriers={carrierFilterOptions}
                />
                <ArchivedToggle
                  on={isArchivedOnly(values)}
                  onChange={(on) =>
                    setFilters({ archived: on ? ARCHIVED_ONLY : "" })
                  }
                />
                {user?.role !== "oneTimeShipper" &&
                user?.user_approved_warehouses?.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => navigate("/orders/scans")}
                    title="What the warehouse app scanned: items, labels, carrier pickups"
                    className="flex items-center gap-[5px] whitespace-nowrap rounded-md border-[1px] border-ui-border bg-ui-surface px-[13px] py-[5px] text-xs font-semibold text-ui-text-muted transition-colors hover:border-brand hover:bg-brand-light hover:text-brand"
                  >
                    <MdQrCodeScanner className="h-3 w-3 shrink-0" />
                    Scans
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setImport(true)}
                  className="flex items-center gap-[5px] whitespace-nowrap rounded-md border-[1px] border-ui-border bg-ui-surface px-[13px] py-[5px] text-xs font-semibold text-ui-text-muted transition-colors hover:border-brand hover:bg-brand-light hover:text-brand"
                >
                  <AiOutlineUpload className="h-3 w-3 shrink-0" />
                  Import
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/sales")}
                  className="flex items-center gap-[5px] whitespace-nowrap rounded-md border border-brand bg-brand px-[13px] py-[5px] text-xs font-semibold text-white transition-colors hover:bg-brand-hover"
                >
                  <MdAdd className="h-3 w-3 shrink-0" />
                  Add order
                </button>
              </div>
            );
          }}
          belowToolbar={({ values, setFilters }) =>
            isSelfDelivery(values) && routesReady ? (
              <RoutesBar
                routes={routes}
                allCount={selfDeliveryCount}
                selected={values?.route_name ?? ""}
                onSelect={(value) => setFilters({ route_name: value })}
                onAdd={() => setRoutesOpen(true)}
                onManage={() => setRoutesOpen(true)}
                onRuns={runsSupported ? () => setRunsOpen(true) : undefined}
              />
            ) : null
          }
        />

        <BulkActionBar
          count={selectedRows.length}
          onClear={() => setSelectedRows([])}
        >
          <div className="relative">
            <BulkActionButton
              label={
                bulkLabelProgress ? `LABELS (${bulkLabelProgress})` : "LABELS"
              }
              onClick={() => setShowLabelOptions(!showLabelOptions)}
              disabled={isBulkLabelMerging}
              icon={
                isBulkLabelMerging
                  ? () => (
                      <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )
                  : null
              }
            />
            {showLabelOptions && (
              <div className="absolute bottom-full left-0 mb-2 w-48 bg-ui-surface border border-ui-border rounded-lg shadow-xl z-[999] overflow-hidden animate-in fade-in zoom-in duration-200">
                <button
                  onClick={handleBulkLabelMerged}
                  className="w-full text-left px-4 py-2.5 text-[10px] font-jakarta font-bold text-ui-text hover:bg-ui-row-hover hover:text-brand transition-colors border-b border-ui-border"
                >
                  DOWNLOAD MERGED PDF
                </button>
                <button
                  onClick={handleBulkLabel}
                  className="w-full text-left px-4 py-2.5 text-[10px] font-jakarta font-bold text-ui-text hover:bg-ui-row-hover hover:text-brand transition-colors"
                >
                  DOWNLOAD SEPARATE PDFS
                </button>
              </div>
            )}
          </div>

          <BulkActionButton
            label="PACKING LISTS"
            onClick={handleBulkPackingList}
          />
          {selectedAreSelfDelivery && runsSupported && canManageRuns && (
            <>
              <BulkActionButton
                label="CREATE RUN"
                onClick={() => setCreateRunIds(selectedRows)}
              />
              <BulkActionButton
                label="ADD TO RUN"
                onClick={() =>
                  setAddToRunRows(
                    (orders?.data ?? []).filter((order) =>
                      selectedRows.includes(order?.id),
                    ),
                  )
                }
              />
            </>
          )}
          {selectedAreSelfDelivery && !runsSupported && (
            <BulkActionButton
              label="ROUTE SHEET"
              onClick={() => setRouteSheetIds(selectedRows)}
            />
          )}
          {canArchive && selectedSplit.notArchived.length > 0 && (
            <BulkActionButton
              label={archiving ? "ARCHIVING..." : "ARCHIVE"}
              onClick={openBulkArchive}
              disabled={archiving}
            />
          )}
          {canArchive && selectedSplit.archived.length > 0 && (
            <BulkActionButton
              label={unarchiving ? "UNARCHIVING..." : "UNARCHIVE"}
              onClick={handleBulkUnarchive}
              disabled={unarchiving}
            />
          )}
        </BulkActionBar>
        <CreateRunModal
          open={Boolean(createRunIds)}
          orderIds={createRunIds ?? []}
          locations={routeLocations}
          defaultLocationId={routeLocations[0]?.id ?? null}
          onCreated={(run) => {
            setCreateRunIds(null);
            setCreateRunSolo(false);
            if (!createRunSolo) {
              runSaved(run, "Run created");
              return;
            }
            if (run?.id == null) return;
            applyRun(run);
            setSnack((snack) => ({
              ...snack,
              open: true,
              text: `Run created: ${runTitle(run)}`,
              severity: "success",
            }));
          }}
          onClose={() => {
            setCreateRunIds(null);
            setCreateRunSolo(false);
          }}
        />
        <AddToRunModal
          open={Boolean(addToRunRows)}
          orders={addToRunRows ?? []}
          locations={routeLocations}
          onAdded={(run, sorting) => {
            setAddToRunRows(null);
            runSaved(run, addedText("Orders", "the run", sorting));
          }}
          onClose={() => setAddToRunRows(null)}
        />
        <RunsModal
          open={runsOpen}
          onOpenRun={(id) => {
            setRunsOpen(false);
            setOpenRunId(id);
          }}
          onClose={() => setRunsOpen(false)}
        />
        <RouteSheetModal
          open={Boolean(routeSheetIds) || openRunId != null}
          orderIds={routeSheetIds ?? []}
          runId={openRunId}
          onRunChanged={applyRun}
          onShowOrders={showRunOrders}
          canManageRuns={canManageRuns}
          canDriveRuns={canDriveRuns}
          locations={routeLocations}
          defaultLocationId={routeLocations[0]?.id ?? null}
          statuses={orderStatus}
          // Same rule as the Fulfilment column's status dropdown.
          canFulfil={(order) =>
            fulfillableWarehouseIds.has(String(order?.warehouse_id))
          }
          // A stop marked on the delivery list: update its row, as the
          // order view's status pop-up does.
          onOrderChanged={(orderId, patch) =>
            setOrders((prevOrders) => ({
              ...prevOrders,
              data: prevOrders?.data?.map((order) =>
                order?.id === orderId
                  ? {
                      ...order,
                      ...Object.fromEntries(
                        Object.entries(patch).filter(
                          ([, value]) => value !== undefined,
                        ),
                      ),
                    }
                  : order,
              ),
            }))
          }
          // An order number on the sheet opens the order view on top of it.
          onOpenOrder={(order) => {
            setDetailFromSheet(order);
            setdetailModal([order?.id, false, undefined, true]);
          }}
          // A stop's status dropdown: what the Fulfilment column's does (the
          // order view with the status pop-up), on top of the sheet.
          onStatusChange={(event, order) => {
            setDetailFromSheet(order);
            handlestatusChange(event, order);
          }}
          orderViewOpen={Boolean(detailModal)}
          onClose={() => {
            setRouteSheetIds(null);
            setOpenRunId(null);
          }}
        />
      </div>
    </div>
  );
};

export default Orders;
